import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  Chip
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { fetchCourseVersionsData } from "../../../../redux/actions/contentCoursesActions";
import { PropTypes } from "prop-types";
import Table from "../../../../components/Table/Table";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import {
  buildCourseSessionRoute,
  buildScheduleSessionRoute
} from "../../../../routes/AdminRoutes";
import { fetchTableData } from "../../../../redux/utils/jsona";
import { contentHosts } from "../../../../constants";
import makeStyles from "@mui/styles/makeStyles";
import { formatDate } from "../../../../utils/datetime";
import { cloneDeep } from "lodash";

const getFilterValues = queryData => {
  const tableQueryData = cloneDeep(queryData);
  if (queryData.filters) {
    const dateFilterIndex = tableQueryData.filters.findIndex(
      filter => filter.column.title === "Date"
    );
    if (dateFilterIndex > -1)
      tableQueryData.filters[dateFilterIndex].value = encodeURIComponent(
        formatDate(tableQueryData.filters[dateFilterIndex].value, "YYYY/MM/DD")
      );
  }
  return tableQueryData;
};

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "no-wrap",
    "& > *": {
      margin: theme.spacing(0.5)
    }
  }
}));

const getRecordsScoreText = (t, passingScore, paperBasedTest) => {
  return passingScore > 0
    ? `${t("yes")} - ${
        paperBasedTest
          ? t("manageCourses.courseSessionsPaperBased")
          : `${passingScore}%`
      }`
    : t("no");
};

const SessionDateCell = ({ date, iconText }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Box component="span" alignItems="center" className={classes.root}>
      <Typography variant="body2">{date}</Typography>
      {iconText && iconText !== contentHosts.visionHosted && (
        <Chip label={t("manageCourses.legacyChip")} color="primary" disabled />
      )}
    </Box>
  );
};

const RecordsScoreCell = ({ passingScore, paperBasedTest }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="body2">
      {getRecordsScoreText(t, passingScore, paperBasedTest)}
    </Typography>
  );
};

const getCoursesTableColumns = t => [
  {
    title: "Date",
    field: "date",
    options: {
      sorting: true,
      filtering: true,
      filterType: "date"
    },
    filterPlaceholder: "dateAll",
    cellStyle: {
      minWidth: 200,
      maxWidth: 200
    },
    render: rowData => (
      <SessionDateCell date={rowData.date} iconText={rowData.contentHost} />
    )
  },
  {
    title: "Location",
    field: "location",
    options: {
      sorting: true,
      filtering: true
    },
    filterPlaceholder: "locationAll",
    cellStyle: {
      minWidth: 180,
      textTransform: "capitalize"
    }
  },
  {
    title: "Instructor",
    field: "instructor",
    cellStyle: {
      minWidth: 120
    },
    options: {
      sorting: true
    }
  },
  {
    title: "recordsScore",
    cellStyle: {
      minWidth: 160
    },
    options: {
      sorting: true
    },
    render: rowData => (
      <RecordsScoreCell
        passingScore={rowData.passingScore}
        paperBasedTest={rowData.paperBasedTest}
      />
    )
  },
  {
    title: "Enrolled",
    field: "numEnrolled",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 120
    }
  },
  {
    title: "Status",
    field: "status",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 120,
      textTransform: "capitalize"
    },
    render: rowData =>
      rowData.status
        ? t(`manageCourses.coursesStatuses.${rowData.status.toLowerCase()}`)
        : ""
  }
];

const CourseSessions = ({
  match: {
    params: { courseId }
  }
}) => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const [versionStatus, setVersionStatus] = useState("active");

  useEffect(() => {
    updateTable();
  }, [versionStatus]);

  const handleFetchSessions = queryData => {
    return fetchTableData(
      dispatch(
        fetchCourseVersionsData(courseId, getFilterValues(queryData), {
          status: versionStatus
        })
      )
    );
  };

  const handleAddNewSessionClick = () => {
    dispatch(push(buildScheduleSessionRoute(courseId)));
  };

  const updateTable = () =>
    tableRef.current && tableRef.current.onQueryChange();

  const handleRowClick = (e, rowData) => {
    dispatch(push(buildCourseSessionRoute(courseId, rowData.id)));
  };

  const columns = useMemo(() => {
    return getCoursesTableColumns(t);
  }, []);

  const sessionsTable = useMemo(() => {
    return (
      <Table
        tableRef={tableRef}
        columns={columns}
        data={handleFetchSessions}
        itemType="record"
        onRowClick={handleRowClick}
      />
    );
  }, [versionStatus]);

  return (
    <Box display="flex" flexDirection="column" mt={3}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box display="flex" flex={1} alignItems="center">
          <Box mr={3}>
            <Typography variant="h5">
              {t("manageCourses.statusFilterLabel")}
            </Typography>
          </Box>
          <TextField
            select
            value={versionStatus}
            onChange={({ target: { value } }) => setVersionStatus(value)}
            variant="filled"
            InputProps={{
              disableUnderline: true
            }}
            SelectProps={{
              displayEmpty: true
            }}
            fullWidth
            id="status-filter"
            hiddenLabel
          >
            <MenuItem value="">
              {t("manageCourses.courseSessionsFilterAllOption")}
            </MenuItem>
            <MenuItem value="active">
              {t("manageCourses.courseSessionsFilterActiveOption")}
            </MenuItem>
            <MenuItem value="inactive">
              {t("manageCourses.courseSessionsFilterInactiveOption")}
            </MenuItem>
            <MenuItem value="closed">
              {t("manageCourses.courseSessionsFilterClosedOption")}
            </MenuItem>
          </TextField>
        </Box>
        <Box
          flex={1}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            color="primary"
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleAddNewSessionClick(true)}
          >
            {t("manageCourses.courseSessionsAddNewBtn")}
          </Button>
        </Box>
      </Box>
      {sessionsTable}
    </Box>
  );
};

CourseSessions.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    })
  })
};

export default CourseSessions;
